/**
 * Your dex, measured against the meta.
 *
 * The Meta page is a reference: what is good. This turns it into advice: what
 * is good THAT YOU HAVE, and what is worth chasing next.
 *
 * WHAT THE DEX CAN AND CANNOT ANSWER — the whole design rests on this line, so
 * it is stated once here and echoed in the UI copy:
 *
 *   It KNOWS      whether you own the species, its Shadow, its Mega, a hundo.
 *   It NEVER KNOWS the level, the moveset, how many you have, or the IVs.
 *
 * So every claim here is about OWNING one, never about having one BUILT. That
 * is not a hedge, it is the difference between advice and a wrong assertion:
 * a Machamp without Counter and Dynamic Punch is not the best Fighting
 * attacker, and a page that implied otherwise would be confidently wrong in a
 * way nothing could catch. The answer is to say the moveset out loud — the
 * ranking already knows what the good one is, so an owned counter comes with
 * "make sure yours knows this", which is useful precisely BECAUSE we cannot
 * check it.
 *
 * TWO MATCHING LIMITS, both deliberate:
 *
 *   1. NO FORM GRANULARITY. dex_records is keyed by national number, so a
 *      Galarian Darmanitan is indistinguishable from an ordinary one. A
 *      regional form therefore collapses to its species: owning any Darmanitan
 *      reads as owning the counter. Mega and Shadow are exempt — they are
 *      their own dexes and are matched exactly.
 *   2. XXL IS A SIZE MEDAL, NOT XL CANDY. Nothing here reads the xxs/xxl
 *      dexes, and nothing here should: they say a Pokémon was unusually large,
 *      not that it can reach level 50.
 *
 * Pure and storage-agnostic: ownership arrives as a callback, so the whole
 * thing is testable without a dex store or a DOM.
 */
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import {
  NO_RAID_FILTERS,
  RAID_TYPE_ORDER,
  rankRaidAttackers,
  type RaidRanked,
} from "./raidMeta";
import type { RaidBossDto } from "./raidRoster";

/** Answers "does the trainer own this?" for one dex variant and species. */
export type OwnsFn = (variant: PokedexVariant, dexId: number) => boolean;

/**
 * The dex a ranked row should be checked against.
 *
 * A Mega raid fills the MEGA dex and a Shadow raid fills the SHADOW dex, so
 * checking either against the base dex would call it owned for anyone holding
 * the ordinary species — the exact false positive that makes a coverage page
 * worthless. Mirrors raidBossVariant() on the boss side.
 */
export function coverageVariantFor(row: Pick<RaidRanked, "shadow" | "attacker">): PokedexVariant {
  if (row.shadow) return "shadow";
  if (row.attacker.form?.startsWith("Mega")) return "mega";
  return "pokemon";
}

/** True when the trainer owns the thing this row is actually describing. */
export function ownsRow(row: RaidRanked, owns: OwnsFn): boolean {
  return owns(coverageVariantFor(row), row.attacker.dexId);
}

export interface CounterCoverage {
  row: RaidRanked;
  owned: boolean;
}

export interface BossCoverage {
  boss: RaidBossDto;
  /** The top counters considered, in order, each marked owned or not. */
  counters: CounterCoverage[];
  ownedCount: number;
  /** How many counters were considered — the denominator in "4 of 10". */
  outOf: number;
  /** The strongest counter the trainer does NOT own, or null when covered. */
  bestMissing: RaidRanked | null;
  /** The strongest counter they DO own, which is the one to actually build. */
  bestOwned: RaidRanked | null;
}

/**
 * How well the trainer is set up for one boss.
 *
 * Ranked by Rating rather than raw DPS: the question is "can I beat this",
 * which a glass cannon that faints twice answers worse than its DPS suggests.
 */
export function bossCoverage(
  boss: RaidBossDto,
  owns: OwnsFn,
  depth = 10,
): BossCoverage | null {
  const types = boss.types ?? [];
  // A boss with no typing cannot be ranked against, so it gets no card rather
  // than a card computed against nothing.
  if (types.length === 0) return null;

  const { rows } = rankRaidAttackers(
    { ...NO_RAID_FILTERS, bossTypes: [...types], bossLabel: boss.name },
    "er",
    depth,
  );

  const counters = rows.map((row) => ({ row, owned: ownsRow(row, owns) }));
  return {
    boss,
    counters,
    ownedCount: counters.filter((c) => c.owned).length,
    outOf: counters.length,
    bestMissing: counters.find((c) => !c.owned)?.row ?? null,
    bestOwned: counters.find((c) => c.owned)?.row ?? null,
  };
}


export interface ShadowUpgrade {
  row: RaidRanked;
  /** Where the Shadow sits in the overall (no boss) Rating ranking. */
  rank: number;
  /**
   * The thinnest type this Shadow would strengthen, and where it would slot
   * in. Null when it is a strong attacker that does not happen to plug a hole
   * — still worth having, just not worth leading with.
   */
  fills: {
    type: string;
    /** Its position among that type's top counters. */
    rankInType: number;
    /** How many of that type's top counters you own RIGHT NOW, without it. */
    ownedInType: number;
    outOf: number;
  } | null;
}

/**
 * Shadows worth chasing: you have the Pokémon, but not its Shadow.
 *
 * The cleanest insight on the page, because it is the one the dex answers
 * EXACTLY — base owned, shadow not — with no inference about level or moves.
 * The payoff is concrete too: a Shadow is +20% attack, so this is a real
 * upgrade to something already in the bag, available from a Shadow raid.
 *
 * Deliberately excludes shadows of species the trainer does not have at all.
 * Those are not upgrades, they are just more of the ranking, which the Raids
 * mode already shows in full.
 */
export function shadowUpgrades(
  owns: OwnsFn,
  rankings: readonly TypeRanking[],
  limit = 8,
  pool = 150,
): ShadowUpgrade[] {
  /** Have you got the ordinary one but not the Shadow? */
  const isUpgrade = (row: RaidRanked) =>
    row.shadow &&
    !owns("shadow", row.attacker.dexId) &&
    owns("pokemon", row.attacker.dexId);

  // ── Candidates that plug a hole ─────────────────────────────────────────
  // Ordered by HOW THIN THE TYPE IS, not by how good the Shadow is overall.
  // Ordering by overall Rating put legendaries at the top — the Regigigas and
  // Reshiram you own exactly one of and will likely never get a Shadow of —
  // above a Shadow of something common that would genuinely fix a gap. The
  // useful question is not "what is strongest" (the Raids tab answers that)
  // but "what would most improve the collection I have".
  const gapFillers: ShadowUpgrade[] = [];
  for (const { type, rows } of rankings) {
    const ownedInType = rows.filter((r) => r.owned).length;
    for (const [i, { row }] of rows.entries()) {
      if (!isUpgrade(row)) continue;
      gapFillers.push({
        row,
        rank: row.rank,
        fills: { type, rankInType: i + 1, ownedInType, outOf: rows.length },
      });
    }
  }
  gapFillers.sort(
    (a, b) =>
      // Thinnest type first, then the better counter within it.
      a.fills!.ownedInType - b.fills!.ownedInType ||
      a.fills!.rankInType - b.fills!.rankInType,
  );

  // ── Fallback tail ───────────────────────────────────────────────────────
  // Strong Shadows that plug no top-ten hole. Kept so a well-covered trainer
  // still gets suggestions, but ranked below anything that fixes something.
  const { rows: neutral } = rankRaidAttackers(NO_RAID_FILTERS, "er", pool);
  const tail: ShadowUpgrade[] = neutral
    .filter(isUpgrade)
    .map((row) => ({ row, rank: row.rank, fills: null }));

  // ONE ROW PER SPECIES, across both lists. Fifty shadow-capable species are
  // in the rankings twice — a base form and a regional one, with their own
  // stats and typing — but the shadow dex is keyed by national number and
  // cannot tell them apart. Listing both claims a distinction we do not have;
  // whichever ranks first stands for the species.
  const out: ShadowUpgrade[] = [];
  const seen = new Set<number>();
  for (const u of [...gapFillers, ...tail]) {
    if (seen.has(u.row.attacker.dexId)) continue;
    seen.add(u.row.attacker.dexId);
    out.push(u);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * The moveset line for an owned counter.
 *
 * This exists BECAUSE the dex cannot see movesets. Rather than hedge the
 * claim into uselessness, the page says what the good one runs and lets the
 * trainer check — which is the actionable half either way, since a counter
 * with the wrong moves is the most common reason a "good" attacker
 * underperforms.
 */
export function movesetCallout(row: RaidRanked): string | null {
  if (!row.fast || !row.charged) return null;
  const a = row.attacker;
  const who = row.shadow ? `Shadow ${a.name}` : a.name;
  // A REGIONAL OR ALTERNATE FORM IS PART OF THE REQUIREMENT, not part of what
  // we know you own. The dex is keyed by national number, so owning a Shaymin
  // reads as owning Shaymin (Sky) — saying "your Shaymin (Sky)" would assert a
  // form we cannot see. Naming the form as something to check keeps the claim
  // true and the advice complete, exactly as the moveset does.
  //
  // Mega is exempt: it has its own dex, so ownership there is already exact.
  const formNeeded = a.form && !a.form.startsWith("Mega") ? a.form : null;
  const needs = formNeeded
    ? `is the ${formNeeded} form and knows ${row.fast.name} and ${row.charged.name}`
    : `knows ${row.fast.name} and ${row.charged.name}`;
  return `Make sure your strongest ${who} ${needs}.`;
}


export interface TypeCoverage {
  /** The BOSS type this measures readiness against. */
  type: string;
  ownedCount: number;
  outOf: number;
  /** 0..1, for the heat ramp. */
  ratio: number;
  /** The strongest counter of this type you own, and the best you do not. */
  bestOwned: RaidRanked | null;
  bestMissing: RaidRanked | null;
  /**
   * The type's PODIUM: its ten best counters in order, each marked owned or
   * not. Shown when a cell is tapped. Three until 2026-09-10; the owner asked
   * for the whole top ten once ownership started reading off My Pokémon,
   * because the comparison is the strong half and ten rows carry it.
   *
   * The ten best FOR THE TYPE, not the ten best you own. A trainer opening a
   * thin type wants to know what the answer is before they know which part of
   * it they have, and marking ownership across the podium says both at once.
   * `bestOwned` still carries the other question for when what they own sits
   * outside these ten.
   */
  top: CounterCoverage[];
}

/**
 * Readiness against every boss type.
 *
 * The grid answers a question neither other section can: not "can I do the
 * raid that is up", but "where is my collection actually thin". A type with
 * nothing in it is a week of raids you will struggle with the moment one
 * appears, and that is worth knowing BEFORE it does.
 *
 * Measured against each type ALONE, deliberately. A real boss has two types
 * and the multipliers compound, but there is no single dual-type to stand for
 * "Psychic bosses in general" — and a per-type figure is the one that stays
 * true whatever turns up next week.
 *
 * Ranked by Rating rather than DPS for the same reason the boss cards are: the
 * question is whether you can beat it, which a glass cannon that faints twice
 * answers worse than its damage suggests.
 */
export interface TypeRanking {
  type: string;
  /** The type's top counters, in order, each marked owned or not. */
  rows: CounterCoverage[];
}

/**
 * The top counters for every boss type, marked against the trainer's dex.
 *
 * Computed ONCE and shared: both the coverage grid and the shadow-upgrade
 * ordering are questions about the same eighteen rankings, and running them
 * twice would double the work to reach two answers that must agree anyway.
 */
export function perTypeRankings(owns: OwnsFn, depth = 10): TypeRanking[] {
  return RAID_TYPE_ORDER.map((type) => {
    const { rows } = rankRaidAttackers(
      { ...NO_RAID_FILTERS, bossTypes: [type] },
      "er",
      depth,
    );
    return { type, rows: rows.map((row) => ({ row, owned: ownsRow(row, owns) })) };
  });
}

/**
 * The other reading of "the best of a type" (2026-09-17 owner ask): the ten
 * best attackers that ARE this type, ranked neutral (no boss), rather than the
 * ten best AGAINST a boss of this type. The grid used to show only the
 * counters under a "bosses" heading, which read as the wrong list; the two
 * now sit behind a pair of tabs and each says which it is.
 */
export function perTypeAttackerRankings(owns: OwnsFn, depth = 10): TypeRanking[] {
  return RAID_TYPE_ORDER.map((type) => {
    const { rows } = rankRaidAttackers(
      { ...NO_RAID_FILTERS, attackerType: type },
      "er",
      depth,
    );
    return { type, rows: rows.map((row) => ({ row, owned: ownsRow(row, owns) })) };
  });
}

/** Which reading of the type grid is up: attackers OF the type, or counters AGAINST it. */
export type TypeCoverageMode = "attackers" | "counters";

/** How many counters the tapped-cell detail shows. */
export const TYPE_PODIUM_SIZE = 10;

export function typeCoverage(rankings: readonly TypeRanking[]): TypeCoverage[] {
  return rankings.map(({ type, rows }) => {
    const ownedCount = rows.filter((m) => m.owned).length;
    return {
      type,
      ownedCount,
      outOf: rows.length,
      ratio: rows.length > 0 ? ownedCount / rows.length : 0,
      bestOwned: rows.find((m) => m.owned)?.row ?? null,
      bestMissing: rows.find((m) => !m.owned)?.row ?? null,
      top: rows.slice(0, TYPE_PODIUM_SIZE),
    };
  });
}

/**
 * The headline over the grid: how many types you are actually ready for, and
 * where the holes are.
 *
 * "Covered" is deliberately a LOW bar — one of the top ten. It is the
 * difference between "I have something for this" and "I have nothing", which
 * is the distinction worth leading with; the grid itself shows the depth.
 */
export function coverageSummary(grid: readonly TypeCoverage[]): {
  covered: number;
  total: number;
  weakest: TypeCoverage[];
  /** True when every type has at least one owned counter. */
  complete: boolean;
} {
  const covered = grid.filter((t) => t.ownedCount > 0).length;
  const weakest = [...grid]
    .sort((a, b) => a.ownedCount - b.ownedCount || a.type.localeCompare(b.type))
    .slice(0, 3);
  return { covered, total: grid.length, weakest, complete: covered === grid.length };
}
